import React, { useCallback, useMemo } from 'react';

import type {
  ProposalDetail,
  ProposedAction,
} from '../../lib/proposals/core/types';
import { useRenderedActionCount } from '../../lib/proposals/hooks/ActionDisplayNumberContext';
import { useMergedActions } from '../../lib/proposals/hooks/useMergedActions';
import { useDeliveryState } from '../../lib/proposals/linkedin/DeliveryStatesContext';
import type { SelectionState } from '../../lib/proposals/types/selection';
import type { TypedActionGroup } from '../../lib/proposals/utils/actionGrouping';
import {
  getStableActionKey,
  isSameLogicalAction,
} from '../../lib/proposals/utils/helpers';
import { ActionAccordion } from './ActionAccordion';
import { DeliveryStatusBadge } from './DeliveryStatusBadge';
import { EmailHeaderRight } from './EmailHeaderRight';
import { MergedAccordionEntry } from './MergedAccordionEntry';
import { MixedActionTable } from './MixedActionTable';
import type { MultiActionTimelineProps } from './MultiActionTimeline';
import {
  AccordionActionContent,
  ActionTableWithSchema,
} from './multiActionTimeline.helpers';
import {
  mergeActionUpdate,
  rendersOwnChrome,
} from './multiActionTimeline.utils';

// ─── Extracted group renderer ───────────────────────────────────────────────

interface ActionGroupRendererProps {
  group: TypedActionGroup;
  proposal: ProposalDetail;
  onUpdate: (proposal: ProposalDetail) => void;
  onActionUpdate?: MultiActionTimelineProps['onActionUpdate'];
  isProcessing: boolean;
  selectionState?: SelectionState;
  showCheckboxes: boolean;
  hasOnlyTables: boolean;
  onOpenThread?: () => void;
  optimisticMessage?: string | null;
}

// ─── Extracted per-action accordion item ────────────────────────────────────

interface ActionAccordionItemProps {
  action: ProposedAction;
  proposal: ProposalDetail;
  onUpdate: (proposal: ProposalDetail) => void;
  onActionUpdate?: MultiActionTimelineProps['onActionUpdate'];
  isProcessing: boolean;
  orgId: number;
  showCheckboxes: boolean;
  showNumbers: boolean;
  isSelected: boolean;
  isCheckboxDisabled: boolean;
  disabledReason: string | null;
  handleToggle: (actionId: number) => void;
  onOpenThread?: () => void;
  optimisticMessage?: string | null;
}

/**
 * Renders a single non-merged action inside the accordion shell. Extracted
 * and wrapped in `React.memo` (per FRONTEND_PATTERNS.md stable-reference
 * rules) so that stabilizing `onSelectionChange`/`headerRight` here actually
 * stops sibling items from re-rendering when one item's own props change —
 * memoizing the props alone has no effect unless the receiving component is
 * also memoized.
 */
const ActionAccordionItem = React.memo(function ActionAccordionItem({
  action,
  proposal,
  onUpdate,
  onActionUpdate,
  isProcessing,
  orgId,
  showCheckboxes,
  showNumbers,
  isSelected,
  isCheckboxDisabled,
  disabledReason,
  handleToggle,
  onOpenThread,
  optimisticMessage,
}: ActionAccordionItemProps) {
  const onSelectionChange = useCallback(
    () => handleToggle(action.id),
    [handleToggle, action.id]
  );

  const delivery = useDeliveryState(action.id);

  const headerRight = useMemo(() => {
    const badge = delivery ? (
      <DeliveryStatusBadge state={delivery} />
    ) : undefined;
    const emailRight =
      action.proposed_action_type_name === 'gmail_send' ? (
        <EmailHeaderRight isProcessing={isProcessing} />
      ) : undefined;
    if (badge && emailRight) {
      return (
        <span className='flex items-center gap-2'>
          {badge}
          {emailRight}
        </span>
      );
    }
    return badge ?? emailRight;
  }, [action.proposed_action_type_name, isProcessing, delivery]);

  return (
    <ActionAccordion
      action={action}
      showCheckbox={showCheckboxes}
      showExecutionOrder={showNumbers}
      isSelected={isSelected}
      isCheckboxDisabled={isCheckboxDisabled}
      disabledReason={disabledReason}
      onSelectionChange={onSelectionChange}
      isProcessing={isProcessing}
      orgId={orgId}
      headerRight={headerRight}
    >
      <AccordionActionContent
        action={action}
        proposal={proposal}
        onUpdate={onUpdate}
        onActionUpdate={onActionUpdate}
        isProcessing={isProcessing}
        onOpenThread={onOpenThread}
        optimisticMessage={optimisticMessage}
      />
    </ActionAccordion>
  );
});

export function ActionGroupRenderer({
  group,
  proposal,
  onUpdate,
  onActionUpdate,
  isProcessing,
  selectionState,
  showCheckboxes,
  hasOnlyTables,
  onOpenThread,
  optimisticMessage,
}: ActionGroupRendererProps) {
  const handleToggle = useCallback(
    (actionId: number) => {
      selectionState?.toggleAction(actionId);
    },
    [selectionState]
  );

  // Handler for merged card edits — merges the updated action back into the proposal.
  const handleMergedActionUpdate = useCallback(
    (updatedAction: ProposedAction) => {
      if (onActionUpdate) {
        onActionUpdate(existingAction =>
          isSameLogicalAction(existingAction, updatedAction)
            ? updatedAction
            : null
        );
      } else {
        onUpdate(mergeActionUpdate(proposal, updatedAction));
      }
    },
    [onUpdate, onActionUpdate, proposal]
  );

  // Merge create + association actions for accordion rendering.
  // Always called (hooks must not be conditional) but only used in the accordion path.
  const { mergedItems } = useMergedActions(group.actions);

  // Numbers are positions in the WHOLE proposal's rendered list, so whether to
  // show them is a whole-list question too. Gating per group would hide the
  // "1." on a single-card group and start the next group at "2." — the same
  // missing-number confusion the numbering itself was fixed to remove.
  const renderedActionCount = useRenderedActionCount();
  const showNumbers = showCheckboxes && renderedActionCount > 1;

  if (group.viewType === 'mixed') {
    return (
      <div className={hasOnlyTables ? 'flex-1 flex flex-col' : ''}>
        <MixedActionTable
          actions={group.actions}
          onUpdate={onUpdate}
          isProcessing={isProcessing}
          selectionState={selectionState}
          proposal={proposal}
          fillContainer={hasOnlyTables}
        />
      </div>
    );
  }

  if (group.viewType === 'table') {
    return (
      <div className={hasOnlyTables ? 'flex-1 flex flex-col' : ''}>
        <ActionTableWithSchema
          group={group}
          orgId={proposal.org}
          proposal={proposal}
          onUpdate={onUpdate}
          isProcessing={isProcessing}
          selectionState={selectionState}
          fillContainer={hasOnlyTables}
        />
      </div>
    );
  }

  // Accordion view — render merged items.
  return (
    <>
      {mergedItems.map(item => {
        if (item.type === 'merged') {
          return (
            <div key={getStableActionKey(item.merged.primaryAction)}>
              <MergedAccordionEntry
                merged={item.merged}
                showCheckbox={showCheckboxes}
                showExecutionOrder={showNumbers}
                selectionState={selectionState}
                isProcessing={isProcessing}
                orgId={proposal.org}
                proposalId={proposal.id}
                onUpdate={handleMergedActionUpdate}
                sourceTypeName={proposal.source_type_name}
                sourceContext={proposal.source_context}
              />
            </div>
          );
        }

        // Original (non-create) action — render as before.
        const { action } = item;

        // These actions render their own chrome — skip the accordion shell
        if (rendersOwnChrome(action)) {
          return (
            <div key={getStableActionKey(action)}>
              <AccordionActionContent
                action={action}
                proposal={proposal}
                onUpdate={onUpdate}
                onActionUpdate={onActionUpdate}
                isProcessing={isProcessing}
                onOpenThread={onOpenThread}
                optimisticMessage={optimisticMessage}
              />
            </div>
          );
        }

        return (
          <div key={getStableActionKey(action)}>
            <ActionAccordionItem
              action={action}
              proposal={proposal}
              onUpdate={onUpdate}
              onActionUpdate={onActionUpdate}
              isProcessing={isProcessing}
              orgId={proposal.org}
              showCheckboxes={showCheckboxes}
              showNumbers={showNumbers}
              isSelected={selectionState?.isSelected(action.id) ?? true}
              isCheckboxDisabled={
                selectionState?.isDisabled(action.id) ?? false
              }
              disabledReason={
                selectionState?.getDisabledReason(action.id) ?? null
              }
              handleToggle={handleToggle}
              onOpenThread={onOpenThread}
              optimisticMessage={optimisticMessage}
            />
          </div>
        );
      })}
    </>
  );
}
